import { Button } from '@yellowcard/components-library-vue3';
import { defineComponent, onMounted, ref, onBeforeUnmount, computed, watchEffect, } from 'vue';
import { useKycStore } from '@/store/modules/kyc';
import { storeToRefs } from 'pinia';
import { KycTypes } from '@/types/kyc';
import '@smile_identity/smart-camera-web';
import { useAuthStore } from '@/store/modules/auth';
import LoaderComponent from '@/components/general/LoaderComponent.vue';
import useTransactionsStore from '@/store/modules/transactions';
import { CountryTypes } from '@/types/country';
export default defineComponent({
    name: 'SubmitDocs',
    computed: {
        KycTypes() {
            return KycTypes;
        },
    },
    props: {},
    components: {
        LoaderComponent,
        Button,
    },
    setup() {
        const kycStore = useKycStore();
        const authStore = useAuthStore();
        const { currentStep, kycStatus, idType } = storeToRefs(kycStore);
        const { user, partnerSessionId, deviceId, token } = storeToRefs(authStore);
        const transactionsStore = useTransactionsStore();
        const { loadingMessage, countryConfig, activeSupportedCountryConfig } = storeToRefs(transactionsStore);
        const { country } = countryConfig.value;
        const isButtonDisabled = ref(true);
        const partnerParams = ref();
        const loading = ref(false);
        let dataToUpload = [];
        const idUpload = [];
        const scrollMessage = ref(`Scroll down to click the 'Take selfie' button to proceed`);
        const showSubHeading = ref(false);
        const showButton = ref(false);
        const isMobileDevice = ref(false);
        const captureType = computed(() => {
            const idTypeConfig = activeSupportedCountryConfig.value?.idTypeValidation.find(config => config.idType === idType.value);
            return idTypeConfig?.captureType;
        });
        const updateDeviceType = () => {
            isMobileDevice.value = window.matchMedia('(max-width: 767px)').matches;
        };
        watchEffect(() => {
            updateDeviceType();
        });
        const documentCaptureModes = computed(() => isMobileDevice.value ? 'camera,upload' : 'upload');
        onMounted(() => {
            updateDeviceType();
            window.addEventListener('resize', updateDeviceType);
            const app = document.querySelector('#smart-camera-web');
            const shadowRootStyle = document.createElement('style');
            shadowRootStyle.textContent = `
      @media screen and (min-width: 600px) and (min-height: 700px) and (max-height: 1000px) {
        * {
          font-family: Jost, sans-serif;
        }

        .flow label.button {
          margin-top: 0;
          padding: 5px 5px;
        }

        #camera-screen {
          margin-top: 0px;
          margin-bottom: 3px;
        }

        #camera-screen > h1, #review-screen > h1, #id-camera-screen > h1, #id-review-screen > h1 {
          margin-top: 3px;
          margin-bottom: 0px;
        }

        #start-image-capture, #select-selfie, #restart-image-capture, #upload-photo-label, #upload-photo {
          padding: 5px 5px;
          font-size: 14px;
          width: 130px;
          margin-top: 5px;
          margin-bottom: 3px;
        }

        #camera-screen > div, #review-screen > div, #id-camera-screen > div {
          padding: 3px;
          border: none !important;
          padding-bottom: 0px;
          margin-bottom: 0px;
        }

        #camera-screen .tips {
          margin-top: 3px;
        }

        .selfie-review-image {
          height: 175px;
          margin: auto;
        }

        #review-screen p {
          margin-top: 1px !important;
          margin-bottom: 1px !important;
        }

        #id-entry-screen {
          padding-top: 0;
        }

        #id-entry-screen h1 {
          margin-top: 0px;
          margin-bottom: 0px;
        }

        #id-entry-screen p {
          font-size: 13px;
        }

        #id-entry-screen div:first-child {
          margin-top: 0px;
        }

        #id-entry-screen .document-tips {
          margin-top: 10px;
        }

        #back-of-id-entry-screen {
          padding-top: 0;
        }

        #back-of-id-entry-screen h1 {
          margin-top: 0px;
          margin-bottom: 0px;
        }

        #back-of-id-entry-screen p {
          font-size: 13px;
        }

        #back-of-id-entry-screen div:first-child {
          margin-top: 0px;
        }

        #back-of-id-entry-screen .flow {
          margin-top: 0;
        }

        #back-of-id-entry-screen #skip-this-step {
          display: none;
        }

        #upload-photo-label {
          margin: auto;
        }

        .id-video-container, .id-video-container.landscape {
          min-height: 250px !important;
          height: 250px !important;
        }
      }
      `;
            app?.shadowRoot?.appendChild(shadowRootStyle);
            const cameraButton = app?.shadowRoot?.getElementById('request-camera-access');
            const imageCaptureButton = app?.shadowRoot?.getElementById('start-image-capture');
            imageCaptureButton?.addEventListener('click', () => {
                scrollMessage.value = `Scroll down to review your selfie`;
            });
            const selectSelfieButton = app?.shadowRoot?.getElementById('select-selfie');
            const uploadPhotoSpan = app?.shadowRoot?.getElementById('upload-photo');
            cameraButton?.addEventListener('click', () => {
                showSubHeading.value = true;
            });
            selectSelfieButton?.addEventListener('click', () => {
                scrollMessage.value = `Scroll down to click the 'Upload Photo' button to proceed`;
            });
            uploadPhotoSpan?.addEventListener('click', () => {
                showSubHeading.value = false;
            });
            app?.addEventListener('imagesComputed', async (e) => {
                try {
                    const data = e.detail;
                    partnerParams.value = data.partner_params;
                    isButtonDisabled.value = false;
                    dataToUpload = await Promise.all(data.images.map(async (image, index) => {
                        const blob = `data:image/jpg;base64,${image.image}`;
                        const file = await fetch(blob).then(r => r.blob());
                        switch (idType.value) {
                            case CountryTypes.IdTypes.NIN:
                            case CountryTypes.IdTypes.ID_BOOK:
                            case CountryTypes.IdTypes.NATIONAL_ID:
                            case CountryTypes.IdTypes.ALIEN_CARD:
                            case CountryTypes.IdTypes.ECOWAS_ID:
                            case CountryTypes.IdTypes.PASSPORT:
                                if ([
                                    KycTypes.SmileDocTypes.ID_CARD_BASE64,
                                    KycTypes.SmileDocTypes.ID_BACK_BASE64,
                                ].includes(KycTypes.docType[image.image_type_id])) {
                                    const idUploadData = {
                                        type: image.image_type_id === 3 ? 'front' : 'back',
                                        file,
                                        image_type_id: image.image_type_id,
                                        user: user.value?.id,
                                        index,
                                    };
                                    idUpload.push(idUploadData);
                                }
                                break;
                            default:
                        }
                        const uploadData = {
                            type: KycTypes.docType[image.image_type_id],
                            file,
                            image_type_id: image.image_type_id,
                            user: user.value?.id,
                            index,
                        };
                        return uploadData;
                    }));
                    showButton.value = true;
                }
                catch (error) {
                    console.error(error);
                }
            });
        });
        onBeforeUnmount(() => {
            // disable camera
            let videoElement;
            try {
                [videoElement] = document.getElementsByClassName('modal-body-smile-container')[0]
                    .children[0].videoContainer.children;
            }
            catch {
                return;
            }
            if (!videoElement)
                return;
            const stream = videoElement.captureStream();
            const tracks = stream.getTracks();
            tracks.forEach((track) => track.stop());
        });
        const proceed = async () => {
            try {
                if (dataToUpload.length === 0 || !user.value) {
                    throw new Error('Cannot submit kyc without images and user');
                }
                loadingMessage.value = 'Submitting KYC';
                loading.value = true;
                isButtonDisabled.value = true;
                await authStore.CheckAccessToken();
                await kycStore.BeginKYC(dataToUpload, user.value, partnerSessionId.value, deviceId.value, partnerParams.value, token.value, idUpload);
                authStore.setUserState({
                    ...user.value,
                    kycStatus: KycTypes.KycStatus.UNDER_REVIEW,
                    createdAt: new Date().toString(),
                });
                kycStore.resetStore();
                currentStep.value = KycTypes.KycSteps.COMPLETE;
                kycStatus.value = KycTypes.KycStatus.UNDER_REVIEW;
                loading.value = false;
            }
            catch (e) {
                isButtonDisabled.value = false;
                loading.value = false;
                console.error(e);
            }
        };
        const backClick = () => {
            currentStep.value = KycTypes.KycSteps.KYC_7;
        };
        const onKeyPress = () => {
            console.log('key pressed');
        };
        return {
            backClick,
            isButtonDisabled,
            proceed,
            onKeyPress,
            loading,
            idType,
            showSubHeading,
            scrollMessage,
            showButton,
            country,
            CountryTypes,
            isMobileDevice,
            documentCaptureModes,
            captureType,
        };
    },
});
